import { postApi, fetchApi, ADMIN_API } from '@/api/client'

const BASE = `${ADMIN_API}/attribution`
const REPORT_BASE = `${ADMIN_API}/monitoring/attribution`

// ============================================================================
// Response types
// ============================================================================

export interface CampaignAttributionResult {
  status: string
  totalCampaigns: number
  successfulCampaigns: number
  failedCampaigns: number
  skippedCampaigns: number
  successRate: number
  totalAttributedConversions: number
  totalAttributedRevenue: number
  mongoDocumentsSynced: number
}

export interface CampaignAttributionResponse {
  success: boolean
  accountId?: number
  result?: CampaignAttributionResult
  error?: string
  message?: string
}

export interface JourneyAttributionResponse {
  success: boolean
  accountId?: number
  journeyId?: string
  attributionDate?: string
  totalJourneys?: number
  processed?: number
  failed?: number
  summariesSaved?: number
  error?: string
  message?: string
}

export interface AttributionHealthResponse {
  status: string
  service: string
}

// ============================================================================
// Campaign attribution
// ============================================================================

export async function triggerCampaignAttribution(accountId: number): Promise<CampaignAttributionResponse> {
  return postApi<CampaignAttributionResponse>(`${BASE}/trigger/${accountId}`)
}

export async function triggerCampaignAttributionAll(): Promise<CampaignAttributionResponse> {
  return postApi<CampaignAttributionResponse>(`${BASE}/trigger/all`)
}

// ============================================================================
// Journey attribution
// ============================================================================

export async function triggerJourneyAttribution(
  accountId: number,
  date?: string,
): Promise<JourneyAttributionResponse> {
  const q = date ? `?date=${date}` : ''
  return postApi<JourneyAttributionResponse>(`${BASE}/journey/trigger/${accountId}${q}`)
}

export async function triggerJourneyAttributionById(
  journeyId: string,
  date?: string,
): Promise<JourneyAttributionResponse> {
  const q = date ? `?date=${date}` : ''
  return postApi<JourneyAttributionResponse>(`${BASE}/journey/${journeyId}/trigger${q}`)
}

export async function triggerJourneyAttributionAll(): Promise<JourneyAttributionResponse> {
  return postApi<JourneyAttributionResponse>(`${BASE}/journey/trigger/all`)
}

// ============================================================================
// Health
// ============================================================================

export async function getAttributionHealth(): Promise<AttributionHealthResponse> {
  return fetchApi<AttributionHealthResponse>(`${BASE}/health`)
}

// ============================================================================
// Shared attribution metrics — camelCase (Jackson serialization from attribution service)
// 4 models × 4 fields each. Model key prefix: lastTouch | firstTouch | linear | timeDecay
// ============================================================================

interface AttributionMetrics {
  lastTouchConversions: number
  lastTouchRevenue: number
  lastTouchAvgRevenue: number
  lastTouchUniqueCustomers: number

  firstTouchConversions: number
  firstTouchRevenue: number
  firstTouchAvgRevenue: number
  firstTouchUniqueCustomers: number

  linearConversions: number
  linearRevenue: number
  linearAvgRevenue: number
  linearUniqueCustomers: number

  timeDecayConversions: number
  timeDecayRevenue: number
  timeDecayAvgRevenue: number
  timeDecayUniqueCustomers: number
}

// Shared engagement fields on both standalone docs and journey campaign nodes
interface EngagementMetrics {
  campaignSent: number
  campaignDelivered: number
  campaignOpened: number | null
  campaignClicked: number
  clickThroughRate: number
  conversionRate: number
  revenuePerTouchpoint: number
  campaignExecutedAt: string
  processingStatus: string
}

// ============================================================================
// campaign_attribution_summary — standalone per-campaign document
// ============================================================================

export interface CampaignAttributionSummary extends AttributionMetrics, EngagementMetrics {
  _id: string
  accId: number
  campaignId: number
  campaignName: string
  campaignType: string
  attributionDate: string
  controlGroupMetrics: unknown | null
  attributionWindowStart: string
  attributionWindowEnd: string
  calculatedAt: string
  version: number
  createdAt: string
  updatedAt: string
}

export interface CampaignAttributionListSummary {
  totalCampaigns: number
  totalLastTouchConversions: number
  totalLastTouchRevenue: number
}

export interface CampaignAttributionListResponse {
  accId: number
  total: number
  offset: number
  limit: number
  summary: CampaignAttributionListSummary
  campaigns: CampaignAttributionSummary[]
}

// ============================================================================
// journey_attribution_summary — journey rollup + embedded campaign nodes
// ============================================================================

// Node inside campaignAttributions[] — same shape as CampaignAttributionSummary
// minus doc-level fields (accId, attributionDate, version, createdAt, updatedAt)
export interface CampaignAttributionBreakdown extends AttributionMetrics, EngagementMetrics {
  campaignId: number
  campaignName: string
  campaignType: string
}

export interface JourneyAttributionSummary extends AttributionMetrics {
  _id: string
  journeyId: string
  journeyName: string
  journeyCode: string
  accId: number
  attributionDate: string
  conversionEvent: string
  conversionDurationDays: number

  totalUsersTouched: number
  totalConversions: number
  overallConversionRate: number

  campaignAttributions: CampaignAttributionBreakdown[]

  attributionWindowStart: string
  attributionWindowEnd: string
  calculatedAt: string
  processingStatus: string
  version: number
  createdAt: string
  updatedAt: string
}

export interface JourneyAttributionListSummary {
  totalJourneys: number
  totalConversions: number
  totalLastTouchRevenue: number
}

export interface JourneyAttributionListResponse {
  accId: number
  total: number
  offset: number
  limit: number
  summary: JourneyAttributionListSummary
  journeys: JourneyAttributionSummary[]
}

// ============================================================================
// Conversion Overview — active / closed split
// ============================================================================

export interface CampaignConversionAttribution {
  lastTouchConversions: number
  lastTouchRevenue: number
  linearConversions: number
  linearRevenue: number
  daysCalculated: number
  lastAttributionDate: string
}

export interface CampaignConversionItem {
  campaignId: number
  campaignName: string
  campaignType: string
  conversionEvent: string
  conversionDurationDays: number
  windowStart: string
  windowEnd: string
  daysRemaining: number
  attribution: CampaignConversionAttribution
}

export interface CampaignConversionOverview {
  accId: number
  activeCount: number
  closedCount: number
  active: CampaignConversionItem[]
  closed: CampaignConversionItem[]
}

export interface JourneyConversionAttribution {
  lastTouchConversions: number
  lastTouchRevenue: number
  linearConversions: number
  linearRevenue: number
  daysCalculated: number
  lastAttributionDate: string
}

export interface JourneyConversionItem {
  journeyId: string
  journeyName: string
  journeyCode: string
  conversionEvent: string
  conversionDurationDays: number
  windowStart: string
  windowEnd: string
  daysRemaining: number
  attribution: JourneyConversionAttribution
}

export interface JourneyConversionOverview {
  accId: number
  activeCount: number
  closedCount: number
  active: JourneyConversionItem[]
  closed: JourneyConversionItem[]
}

// ============================================================================
// Journey what-if compute — POST /attribution/journey/{id}/compute
// ============================================================================

export interface ModelMetrics {
  conversions: number
  uniqueCustomers: number
  revenue: number
  avgRevenue: number
}

export interface JourneyComputeResult {
  journeyId: string
  journeyName: string
  accId: number
  scenario: {
    configuredConversionEvent: string | null
    testedConversionEvent: string
    revenueField: string | null
    /** Conversion filter actually applied (journey config merged with the overrides); null/absent = none. */
    filter?: { attribute: string | null; column: string; logic: string | null; value: string | null } | null
    /** Physical column identifying one conversion (e.g. the policy number); null/absent = one per user. */
    idColumn?: string | null
    /** Only an id's first-ever event counted. */
    firstEventOnly?: boolean
  }
  window: {
    startDate: string
    endDate: string
  }
  funnel: {
    totalUsersTouched: number
    totalConversions: number
    conversionRate: number
  }
  models: {
    lastTouch: ModelMetrics
    firstTouch: ModelMetrics
    linear: ModelMetrics
    timeDecay: ModelMetrics
  }
  campaignBreakdown: Array<{
    campaignId: number
    campaignName: string | null
    touchpoints: number
    lastTouch: ModelMetrics
    firstTouch: ModelMetrics
    linear: ModelMetrics
    timeDecay: ModelMetrics
  }>
}

/**
 * Optional what-if overrides. Omit a field to use the journey's own conversion parameter (so the result matches the
 * nightly job); for filterAttribute / orderIdField an empty string means "none".
 */
export interface ComputeScenarioOverrides {
  filterAttribute?: string
  filterLogic?: string
  filterValue?: string
  orderIdField?: string
  firstEventOnly?: boolean
}

export async function computeJourneyAttribution(params: {
  journeyId: string
  conversionEventName: string
  startDate: string
  endDate: string
  revenueField?: string
} & ComputeScenarioOverrides): Promise<JourneyComputeResult> {
  const q = new URLSearchParams({
    conversionEventName: params.conversionEventName,
    startDate: params.startDate,
    endDate: params.endDate,
  })
  if (params.revenueField?.trim()) q.set('revenueField', params.revenueField.trim())
  if (params.filterAttribute !== undefined) q.set('filterAttribute', params.filterAttribute.trim())
  if (params.filterLogic) q.set('filterLogic', params.filterLogic)
  if (params.filterValue !== undefined) q.set('filterValue', params.filterValue)
  if (params.orderIdField !== undefined) q.set('orderIdField', params.orderIdField.trim())
  if (params.firstEventOnly !== undefined) q.set('firstEventOnly', String(params.firstEventOnly))
  return postApi<JourneyComputeResult>(`${BASE}/journey/${params.journeyId}/compute?${q}`)
}

// ============================================================================
// Report fetch functions
// ============================================================================

export async function getCampaignConversionOverview(accId: number): Promise<CampaignConversionOverview> {
  return fetchApi<CampaignConversionOverview>(`${REPORT_BASE}/campaigns/conversion/overview?accId=${accId}`)
}

export async function getJourneyConversionOverview(accId: number): Promise<JourneyConversionOverview> {
  return fetchApi<JourneyConversionOverview>(`${REPORT_BASE}/journeys/conversion/overview?accId=${accId}`)
}

export async function getAttributionCampaigns(params: {
  accId: number
  limit?: number
  offset?: number
}): Promise<CampaignAttributionListResponse> {
  const q = new URLSearchParams({ accId: String(params.accId) })
  if (params.limit != null) q.set('limit', String(params.limit))
  if (params.offset != null) q.set('offset', String(params.offset))
  return fetchApi<CampaignAttributionListResponse>(`${REPORT_BASE}/campaigns?${q}`)
}

export async function getAttributionJourneys(params: {
  accId: number
  limit?: number
  offset?: number
}): Promise<JourneyAttributionListResponse> {
  const q = new URLSearchParams({ accId: String(params.accId) })
  if (params.limit != null) q.set('limit', String(params.limit))
  if (params.offset != null) q.set('offset', String(params.offset))
  return fetchApi<JourneyAttributionListResponse>(`${REPORT_BASE}?${q}`)
}

export async function getAttributionJourneyById(id: string): Promise<JourneyAttributionSummary> {
  return fetchApi<JourneyAttributionSummary>(`${REPORT_BASE}/${encodeURIComponent(id)}`)
}
